/* Patch 15.3 — Data Quality Center
   ==================================
   The entry point before Reconciliation and Repair: scans every
   source document for arithmetic / VAT / line-sum / orphan-link
   defects and shows them with severity (CRITICAL/HIGH/MEDIUM/LOW).

   For each finding the user can:
     • Preview Correction — read-only diff modal
     • Apply Correction   — explicit human approval
     • Audit Trail        — every apply writes to audit_log

   No auto-fix anywhere. */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, ShieldAlert, CheckCircle2, AlertTriangle,
  XCircle, Activity, Eye, Wrench, ExternalLink, Filter, Zap,
} from 'lucide-react';
import BulkCorrectionWizard from '../components/BulkCorrectionWizard';

const fmt = (n) => {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
};

const SEVERITY_META = {
  critical: { cls: 'bg-rose-100    text-rose-800    border-rose-300',    Icon: XCircle,       order: 0 },
  high:     { cls: 'bg-orange-100  text-orange-800  border-orange-300',  Icon: AlertTriangle, order: 1 },
  medium:   { cls: 'bg-amber-100   text-amber-800   border-amber-300',   Icon: AlertTriangle, order: 2 },
  low:      { cls: 'bg-slate-100   text-slate-700   border-slate-300',   Icon: Activity,      order: 3 },
};

const VERDICT_META = {
  CLEAN:       { cls: 'bg-emerald-50 border-emerald-300', text: 'text-emerald-800', label: 'CLEAN — Data integrity passes all checks' },
  ADVISORY:    { cls: 'bg-sky-50 border-sky-300',         text: 'text-sky-800',     label: 'ADVISORY — Cosmetic findings present' },
  WARNING:     { cls: 'bg-amber-50 border-amber-300',     text: 'text-amber-900',   label: 'WARNING — Reports may understate/overstate' },
  INVESTIGATE: { cls: 'bg-rose-50 border-rose-300',       text: 'text-rose-900',    label: 'INVESTIGATE — Critical defects block GL posting' },
};

const CATEGORY_LABELS = {
  invoice_math:      'Invoice Math Errors',
  bill_math:         'Bill Math Errors',
  invoice_lines:     'Invoice Lines ≠ Subtotal',
  bill_lines:        'Bill Lines ≠ Subtotal',
  vat_rate:          'VAT Rate Mismatch',
  unposted_docs:     'Unposted Documents',
  orphan_cost_lines: 'Orphan Project Cost Lines',
  vat_exceptions:    'VAT Exceptions',
};

export default function DataQualityCenter() {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [previewing, setPreviewing] = useState(null);
  const [showOrphanModal, setShowOrphanModal] = useState(false);
  const [bulkWizard, setBulkWizard] = useState(null);
  const [severityFilter, setSeverityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get('/data-quality/scan');
      setData(r.data);
    } catch (e) {
      toast.error(`Scan failed: ${e.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    if (!data?.findings) return {};
    const g = {};
    for (const f of data.findings) {
      const c = f.category;
      if (!g[c]) g[c] = [];
      g[c].push(f);
    }
    return g;
  }, [data]);

  const filtered = useMemo(() => {
    if (!data?.findings) return [];
    return data.findings.filter(f => {
      if (severityFilter !== 'all' && f.severity !== severityFilter) return false;
      if (categoryFilter !== 'all' && f.category !== categoryFilter) return false;
      return true;
    }).sort((a, b) => (SEVERITY_META[a.severity]?.order ?? 9)
                     - (SEVERITY_META[b.severity]?.order ?? 9));
  }, [data, severityFilter, categoryFilter]);

  const vm = VERDICT_META[data?.verdict] || VERDICT_META.CLEAN;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5" data-testid="data-quality-center-page">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 inline-flex items-center gap-2">
            <ShieldAlert className="text-violet-600" size={26} /> Data Quality Center
          </h1>
          <p className="text-sm text-slate-600">
            The diagnostic layer before Reconciliation & Repair. Scans every source document for arithmetic, VAT, line-sum, and orphan-link defects.
          </p>
        </div>
        <button
          data-testid="dq-refresh-btn"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded px-4 py-1.5 text-sm font-semibold disabled:opacity-50"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Re-Scan
        </button>
      </header>

      {data && (
        <>
          <div className={`rounded-lg p-5 border-2 ${vm.cls}`} data-testid="dq-verdict-banner">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase font-semibold opacity-70">Verdict</div>
                <div className={`text-2xl font-bold mt-1 ${vm.text}`}>{data.verdict}</div>
                <div className="text-sm mt-1 opacity-80">{vm.label}</div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase font-semibold opacity-70">Health Score</div>
                <div className={`text-5xl font-bold mt-1 ${vm.text}`}>
                  {data.health_score}<span className="text-xl opacity-50">/100</span>
                </div>
                <div className="text-xs mt-1">{data.total_findings} total finding(s)</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-3" data-testid="dq-severity-cards">
            {['critical','high','medium','low'].map(sev => {
              const sm = SEVERITY_META[sev];
              const Icon = sm.Icon;
              const c = data.counters[sev] || 0;
              return (
                <button
                  key={sev}
                  data-testid={`dq-sev-${sev}`}
                  onClick={() => setSeverityFilter(severityFilter === sev ? 'all' : sev)}
                  className={`rounded-lg p-3 border text-left transition ${sm.cls} ${
                    severityFilter === sev ? 'ring-2 ring-offset-1 ring-slate-700' : ''
                  } ${c === 0 ? 'opacity-40' : 'hover:shadow'}`}
                >
                  <div className="text-[10px] uppercase tracking-wider opacity-70 inline-flex items-center gap-1">
                    <Icon size={10} /> {sev}
                  </div>
                  <div className="text-3xl font-bold mt-1 font-mono">{c}</div>
                </button>
              );
            })}
          </div>

          <BulkActionsPanel grouped={grouped} onLaunch={setBulkWizard} />

          <div className="bg-white border border-slate-200 rounded-lg p-3 flex items-center gap-3" data-testid="dq-filter-bar">
            <Filter size={14} className="text-slate-500" />
            <label className="text-xs text-slate-700">Category:</label>
            <select
              data-testid="dq-category-filter"
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value)}
              className="text-xs border border-slate-300 rounded px-2 py-1"
            >
              <option value="all">All categories</option>
              {Object.keys(grouped).map(c => (
                <option key={c} value={c}>{CATEGORY_LABELS[c] || c} ({grouped[c].length})</option>
              ))}
            </select>
            <label className="text-xs text-slate-700 ml-2">Severity:</label>
            <select
              data-testid="dq-severity-filter"
              value={severityFilter}
              onChange={e => setSeverityFilter(e.target.value)}
              className="text-xs border border-slate-300 rounded px-2 py-1"
            >
              <option value="all">All severities</option>
              <option value="critical">Critical only</option>
              <option value="high">High only</option>
              <option value="medium">Medium only</option>
              <option value="low">Low only</option>
            </select>
            <div className="ml-auto text-xs text-slate-500">
              Showing {filtered.length} / {data.total_findings}
            </div>
          </div>

          <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            {filtered.length === 0 ? (
              <div className="text-center py-12 text-slate-500 text-sm">
                <CheckCircle2 size={32} className="mx-auto text-emerald-500 mb-2" />
                No findings match the current filters.
              </div>
            ) : (
              <table className="w-full text-xs" data-testid="dq-findings-table">
                <thead>
                  <tr className="bg-slate-50 text-left text-[10px] uppercase text-slate-500">
                    <th className="px-3 py-2 w-20">Severity</th>
                    <th className="px-3 py-2">Category</th>
                    <th className="px-3 py-2">Document</th>
                    <th className="px-3 py-2">Message</th>
                    <th className="px-3 py-2 text-right">Variance</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((f, i) => {
                    const sm = SEVERITY_META[f.severity];
                    const Icon = sm.Icon;
                    return (
                      <tr key={i} className="border-t border-slate-100" data-testid={`dq-finding-${i}`}>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${sm.cls}`}>
                            <Icon size={10} /> {f.severity.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-slate-700">{CATEGORY_LABELS[f.category] || f.category}</td>
                        <td className="px-3 py-2 font-mono">{f.doc_label || '—'}</td>
                        <td className="px-3 py-2 max-w-md text-[11px] text-slate-600">{f.message}</td>
                        <td className="px-3 py-2 text-right font-mono">
                          {f.variance !== undefined ? (
                            <span className="font-bold text-rose-700">
                              {f.variance > 0 ? '+' : ''}{fmt(f.variance)}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {f.suggested_correction ? (
                            <button
                              data-testid={`dq-preview-${i}`}
                              onClick={() => setPreviewing(f)}
                              className="inline-flex items-center gap-1 bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-semibold rounded px-2 py-1"
                            >
                              <Eye size={10} /> Preview Correction
                            </button>
                          ) : f.category === 'unposted_docs' ? (
                            <a
                              href="/financial-repair-center"
                              data-testid={`dq-open-repair-${i}`}
                              className="inline-flex items-center gap-1 bg-amber-600 hover:bg-amber-700 text-white text-[10px] font-semibold rounded px-2 py-1"
                            >
                              <Wrench size={10} /> Open Repair Center
                            </a>
                          ) : f.category === 'orphan_cost_lines' ? (
                            <button
                              data-testid={`dq-link-projects-${i}`}
                              onClick={() => setShowOrphanModal(true)}
                              className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-semibold rounded px-2 py-1"
                            >
                              <Wrench size={10} /> ربط بالمشاريع
                            </button>
                          ) : (
                            <span className="text-[10px] text-slate-400">no auto-correction</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}

      {previewing && (
        <PreviewCorrectionModal
          finding={previewing}
          onClose={() => setPreviewing(null)}
          onApplied={async () => { setPreviewing(null); await load(); }}
        />
      )}

      {showOrphanModal && (
        <OrphanCostLinesModal
          onClose={() => setShowOrphanModal(false)}
          onChanged={load}
        />
      )}

      {bulkWizard && (
        <BulkCorrectionWizard
          category={bulkWizard.category}
          categoryLabel={CATEGORY_LABELS[bulkWizard.category] || bulkWizard.category}
          strategyId={bulkWizard.strategyId}
          findings={bulkWizard.findings}
          onClose={() => setBulkWizard(null)}
          onApplied={load}
        />
      )}
    </div>
  );
}


function BulkActionsPanel({ grouped, onLaunch }) {
  // Show one card per category that has > 1 finding AND a consistent
  // suggested_correction.strategy_id (so all findings can be bulk-fixed
  // by the same strategy).
  const cards = [];
  for (const [cat, items] of Object.entries(grouped)) {
    if (items.length < 2) continue;
    const strategies = new Set();
    for (const f of items) {
      const sid = f.suggested_correction?.strategy_id;
      if (sid) strategies.add(sid);
    }
    if (strategies.size !== 1) continue;
    const [strategyId] = Array.from(strategies);
    if (!strategyId) continue;
    cards.push({
      category:    cat,
      strategyId,
      findings:    items.filter(f => f.suggested_correction),
      count:       items.length,
      severity:    items[0].severity,
    });
  }
  if (cards.length === 0) return null;
  return (
    <div className="bg-violet-50 border border-violet-300 rounded-lg p-4" data-testid="dq-bulk-actions-panel">
      <h3 className="text-sm font-bold text-violet-900 mb-2 inline-flex items-center gap-1">
        <Zap size={14} /> Bulk Actions
        <span className="text-[10px] font-normal text-violet-700 bg-white/70 px-1.5 py-0.5 rounded ml-1">
          Wizard with double-confirmation
        </span>
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {cards.map((c) => (
          <button
            key={c.category}
            data-testid={`bulk-action-${c.category}`}
            onClick={() => onLaunch({
              category:   c.category,
              strategyId: c.strategyId,
              findings:   c.findings,
            })}
            className="bg-white border border-violet-200 rounded p-3 text-left hover:border-violet-500 hover:shadow transition"
          >
            <div className="text-[10px] uppercase tracking-wider text-violet-600">
              {c.severity.toUpperCase()}
            </div>
            <div className="text-sm font-bold text-slate-900">
              {CATEGORY_LABELS[c.category] || c.category}
              <span className="ml-1 text-rose-700 font-mono">({c.count})</span>
            </div>
            <div className="text-[10px] font-mono text-slate-500 mt-0.5 truncate">
              {c.strategyId}
            </div>
            <div className="text-[11px] text-violet-700 mt-1 font-bold">
              Apply All →
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}


function PreviewCorrectionModal({ finding, onClose, onApplied }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axiosInstance.get(
          '/data-quality/preview-correction',
          { params: {
              doc_type:    finding.doc_type,
              doc_id:      finding.doc_id,
              strategy_id: finding.suggested_correction.strategy_id,
            },
          });
        if (!cancelled) setPreview(r.data);
      } catch (e) {
        toast.error(`Preview failed: ${e.response?.data?.detail?.error
          || e.response?.data?.detail || e.message}`);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [finding]);

  const apply = async () => {
    setApplying(true);
    try {
      await axiosInstance.post('/data-quality/apply-correction', {
        doc_type:    finding.doc_type,
        doc_id:      finding.doc_id,
        strategy_id: finding.suggested_correction.strategy_id,
      });
      toast.success('Correction applied ✓ Audit log entry written.');
      await onApplied();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(`Apply failed: ${typeof d === 'object' ? (d.error || JSON.stringify(d)) : (d || e.message)}`);
    } finally { setApplying(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" data-testid="preview-correction-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <header className="px-5 py-3 border-b border-slate-200">
          <div className="text-xs uppercase text-slate-500">Preview Correction</div>
          <h2 className="text-base font-bold text-slate-900">
            {finding.doc_label} — {CATEGORY_LABELS[finding.category] || finding.category}
          </h2>
        </header>
        <div className="p-5 space-y-4">
          <div className="bg-rose-50 border border-rose-200 rounded p-3">
            <div className="text-xs font-bold text-rose-800 mb-1">Problem</div>
            <div className="text-sm text-rose-900">{finding.message}</div>
          </div>

          <div className="bg-indigo-50 border border-indigo-200 rounded p-3">
            <div className="text-xs font-bold text-indigo-800 mb-1">Strategy</div>
            <div className="text-sm text-indigo-900">
              {finding.suggested_correction.description}
            </div>
            <div className="text-[10px] font-mono text-indigo-600 mt-1">
              {finding.suggested_correction.strategy_id}
            </div>
          </div>

          {loading && (
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <Loader2 size={14} className="animate-spin" /> Computing diff…
            </div>
          )}

          {preview && (
            <div className="bg-slate-50 border border-slate-200 rounded overflow-hidden">
              <div className="text-xs font-bold text-slate-700 px-3 py-2 bg-slate-100">
                Diff (Before → After)
              </div>
              <table className="w-full text-xs font-mono" data-testid="preview-diff-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase text-slate-500">
                    <th className="px-3 py-1.5">Field</th>
                    <th className="px-3 py-1.5 text-right">Before</th>
                    <th className="px-3 py-1.5 text-right">After</th>
                    <th className="px-3 py-1.5 text-right">Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(preview.diff || {}).map(([field, vals]) => (
                    <tr key={field} className="border-t border-slate-200">
                      <td className="px-3 py-1.5 font-bold">{field}</td>
                      <td className="px-3 py-1.5 text-right text-rose-700">{fmt(vals.before)}</td>
                      <td className="px-3 py-1.5 text-right text-emerald-700">{fmt(vals.after)}</td>
                      <td className="px-3 py-1.5 text-right font-bold">
                        {fmt((vals.after || 0) - (vals.before || 0))}
                      </td>
                    </tr>
                  ))}
                  {Object.keys(preview.diff || {}).length === 0 && (
                    <tr><td colSpan="4" className="px-3 py-3 text-center text-slate-500">
                      No-op — values already correct.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900">
            <strong>Advisory</strong> — This change updates the source document.
            An audit_log entry is written. The change is reversible only by
            manually editing the source document later.
          </div>
        </div>
        <footer className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button
            data-testid="preview-cancel-btn"
            onClick={onClose}
            className="px-3 py-1.5 text-xs rounded border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            data-testid="preview-apply-btn"
            onClick={apply}
            disabled={applying || loading || !preview?.ok}
            className="px-3 py-1.5 text-xs rounded bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-semibold inline-flex items-center gap-1"
          >
            {applying ? <Loader2 size={12} className="animate-spin" /> : <Wrench size={12} />}
            Approve Correction
          </button>
        </footer>
      </div>
    </div>
  );
}

// ── Orphan Cost Lines — human project assignment ─────────────────────
function OrphanCostLinesModal({ onClose, onChanged }) {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [companyAccounts, setCompanyAccounts] = useState([]);
  const [choice, setChoice] = useState({});
  const [accChoice, setAccChoice] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        axiosInstance.get('/data-quality/orphan-cost-lines'),
        axiosInstance.get('/projects'),
        axiosInstance.get('/accounts'),
      ]);
      setRows(r1.data.items || []);
      setProjects(Array.isArray(r2.data) ? r2.data : []);
      const accs = (Array.isArray(r3.data) ? r3.data : []).filter((a) => {
        const n = String(a.account_number || '');
        const t = String(a.account_type || '').toLowerCase();
        return t.startsWith('expense') && !n.startsWith('51') && !n.startsWith('53');
      });
      setCompanyAccounts(accs);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadRows(); }, [loadRows]);

  const assign = async (jid) => {
    const pid = choice[jid];
    if (!pid) { toast.error('اختر المشروع أولاً'); return; }
    setBusyId(jid);
    try {
      const r = await axiosInstance.post(`/data-quality/orphan-cost-lines/${jid}/assign`, { project_id: pid });
      toast.success(`تم ربط القيد بمشروع ${r.data.project_name}`);
      await loadRows();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusyId(null); }
  };

  const chargeCompany = async (jid) => {
    const aid = accChoice[jid];
    if (!aid) { toast.error('اختر حساب مصاريف الشركة أولاً'); return; }
    setBusyId(jid);
    try {
      const r = await axiosInstance.post(`/data-quality/orphan-cost-lines/${jid}/charge-company`, { account_id: aid });
      toast.success(`تم التحميل على ${r.data.account_name} — قيد ${r.data.reclass_journal_no}`);
      await loadRows();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusyId(null); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="orphan-lines-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">
        <header className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-slate-800 text-sm">سطور تكاليف بلا مشروع — ربط يدوي</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              هذه القيود مصدرها بلا مشروع، والنظام لا يخمّن المشروع أبداً. اختر المشروع الصحيح لكل قيد — الوسم فقط، المبالغ لا تتغير.
            </p>
          </div>
          <button onClick={onClose} data-testid="orphan-modal-close" className="text-slate-400 hover:text-slate-700 text-lg">✕</button>
        </header>
        <div className="overflow-auto p-4">
          {loading && <div className="text-center text-slate-500 py-8 text-sm">جاري التحميل...</div>}
          {!loading && rows.length === 0 && (
            <div className="text-center text-emerald-600 py-8 text-sm font-semibold" data-testid="orphan-empty">
              ✓ لا توجد سطور يتيمة — كل التكاليف مربوطة بمشاريعها
            </div>
          )}
          {!loading && rows.map((r) => (
            <div key={`${r.journal_id}-${r.account_number}-${r.debit}`} className="border border-slate-200 rounded-lg p-3 mb-2 flex flex-wrap items-center gap-3 text-xs">
              <div className="min-w-[110px]">
                <div className="font-mono font-bold">{r.journal_no}</div>
                <div className="text-slate-400 font-mono text-[10px]">{r.journal_date}</div>
              </div>
              <div className="flex-1 min-w-[180px]">
                <div className="text-slate-700">{r.description || '—'}</div>
                <div className="text-slate-400 text-[10px]">
                  <span className="font-mono">{r.account_number}</span> {r.account_name || ''} · {r.reference_type || 'manual'}
                </div>
              </div>
              <div className="font-mono font-bold text-rose-700 min-w-[80px] text-right">
                {(r.debit || 0) > 0 ? (r.debit || 0).toLocaleString() : `(${(r.credit || 0).toLocaleString()})`}
              </div>
              <select
                data-testid={`orphan-select-${r.journal_no}`}
                value={choice[r.journal_id] || ''}
                onChange={(e) => setChoice((c) => ({ ...c, [r.journal_id]: e.target.value }))}
                className="border border-slate-300 rounded px-2 py-1 text-xs min-w-[150px] bg-white"
              >
                <option value="">— اختر المشروع —</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <button
                data-testid={`orphan-assign-${r.journal_no}`}
                onClick={() => assign(r.journal_id)}
                disabled={busyId === r.journal_id}
                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded px-3 py-1 text-xs font-semibold"
              >
                {busyId === r.journal_id ? '...' : 'ربط'}
              </button>
              <span className="text-slate-300 text-[10px]">أو</span>
              <select
                data-testid={`orphan-company-acc-${r.journal_no}`}
                value={accChoice[r.journal_id] || ''}
                onChange={(e) => setAccChoice((c) => ({ ...c, [r.journal_id]: e.target.value }))}
                className="border border-amber-300 rounded px-2 py-1 text-xs min-w-[170px] bg-amber-50"
              >
                <option value="">— حساب مصاريف الشركة —</option>
                {companyAccounts.map((a) => (
                  <option key={a.id} value={a.id}>{a.account_number} · {a.account_name}</option>
                ))}
              </select>
              <button
                data-testid={`orphan-charge-${r.journal_no}`}
                onClick={() => chargeCompany(r.journal_id)}
                disabled={busyId === r.journal_id}
                className="bg-amber-600 hover:bg-amber-700 disabled:opacity-40 text-white rounded px-3 py-1 text-xs font-semibold"
              >
                {busyId === r.journal_id ? '...' : 'تحميل على الشركة'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
